import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2, CalendarClock } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { api, formatApiErrorDetail } from "../lib/api";
import { trackLeadConversion } from "../lib/gtag";
import { HEADER_IMG, CONTACT } from "../data/site";

const inputCls =
  "w-full bg-card border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground rounded-none focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-shadow";
const labelCls = "block font-mono uppercase text-[11px] tracking-[0.15em] text-muted-foreground mb-2";

export default function RequestQuote() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [fileName, setFileName] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData(e.target);
      await api.post("/quote", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackLeadConversion("request-quote");
      setDone(true);
      toast.success("Thank you! Your request has been submitted successfully. Our team will review it and get back to you shortly.");
      e.target.reset();
      setFileName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const meetingHref = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Schedule a Meeting — SourceX Global")}&body=${encodeURIComponent("Hi SourceX Global, I'd like to schedule a video meeting to discuss my requirement.")}`;

  return (
    <div data-testid="quote-page">
      <PageHeader
        overline="Request a Quote"
        title="Get a competitive quote."
        subtitle="Share your requirement and drawing. Our sourcing engineers respond to most RFQs within 24 hours."
        bgImage={HEADER_IMG.quote}
      />
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            data-testid="quote-success" className="border border-border bg-secondary p-12 text-center">
            <CheckCircle2 size={48} className="text-brand-orange mx-auto mb-6" />
            <h2 className="font-display font-black text-3xl mb-3">RFQ Received!</h2>
            <p className="text-muted-foreground mb-8">Our team is reviewing your requirement and will respond with a quote soon.</p>
            <button onClick={() => setDone(false)} className="border border-foreground px-6 py-3 font-mono uppercase text-[11px] tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors">Submit Another</button>
          </motion.div>
        ) : (
          <form onSubmit={onSubmit} data-testid="quote-form" className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div><label className={labelCls}>Name *</label><input data-testid="quote-name" name="name" required className={inputCls} placeholder="Full name" /></div>
              <div><label className={labelCls}>Company *</label><input data-testid="quote-company" name="company" required className={inputCls} placeholder="Your company" /></div>
              <div><label className={labelCls}>Country *</label><input data-testid="quote-country" name="country" required className={inputCls} placeholder="e.g. United Kingdom" /></div>
              <div><label className={labelCls}>Email *</label><input data-testid="quote-email" type="email" name="email" required className={inputCls} placeholder="you@company.com" /></div>
              <div><label className={labelCls}>Phone</label><input data-testid="quote-phone" name="phone" className={inputCls} placeholder="+44 ..." /></div>
              <div><label className={labelCls}>Commodity *</label><input data-testid="quote-commodity" name="commodity" required className={inputCls} placeholder="e.g. Aluminium die casting" /></div>
            </div>
            <div><label className={labelCls}>Quantity *</label><input data-testid="quote-quantity" name="quantity" required className={inputCls} placeholder="e.g. 5,000 units" /></div>
            <div><label className={labelCls}>Message</label><textarea data-testid="quote-message" name="message" rows={4} className={inputCls} placeholder="Material, tolerances, target price, timelines..." /></div>
            <div>
              <label className={labelCls}>Drawing Upload</label>
              <label className="flex items-center gap-3 border border-dashed border-border px-4 py-4 cursor-pointer hover:border-brand-orange transition-colors">
                <Upload size={18} className="text-brand-orange" />
                <span className="text-muted-foreground text-sm truncate">{fileName || "Attach Drawing (PDF, DWG, STEP, PNG)"}</span>
                <input data-testid="quote-file" type="file" name="drawing" accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.step,.stp"
                  className="hidden" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
              </label>
            </div>
            <div className="flex flex-wrap gap-3">
              <button data-testid="quote-submit" type="submit" disabled={loading}
                className="inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting</> : "Submit RFQ"}
              </button>
              <a href={meetingHref} className="inline-flex items-center gap-2 border border-border px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:border-brand-orange hover:text-brand-orange transition-colors">
                <CalendarClock size={15} /> Schedule Meeting
              </a>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
